import { useEffect, useState } from 'react';
import { useParams } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useFiling, useFilingActions } from './state';
import { FilingShell } from './components/FilingShell';

export function FilingReviewPage() {
  const { filingId } = useParams({ strict: false });
  const snapshot = useFiling((s) => s);
  const actions = useFilingActions();
  const [note, setNote] = useState('');
  useEffect(() => {
    if (filingId) actions.loadReview(filingId);
  }, [filingId]);
  const review = snapshot.context.review;
  const rejected = snapshot.context.filings.find((f) => f.filingId === filingId)?.status?.toLowerCase() === 'rejected';
  return (
    <FilingShell title="Filing review">
      {(snapshot.matches('reviewLoading') || snapshot.matches('idle')) && (
        <p className="border border-border bg-card p-5 text-sm text-muted-foreground">Loading review…</p>
      )}
      {snapshot.matches('reviewError') && (
        <p className="border border-destructive/40 bg-card p-5 text-sm">We couldn’t load this filing review.</p>
      )}
      {snapshot.matches('reviewReady') && review && (
        <div className="space-y-6">
          <div className="border border-border bg-card p-5">
            <p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">Filing identifier</p>
            <p className="mt-2 text-lg font-medium">{review.filingId}</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Review the record and document any correction required.
            </p>
          </div>
          <div className="border border-border bg-card p-5">
            <label htmlFor="correction-note" className="text-sm font-medium">
              Correction note
            </label>
            <Textarea
              id="correction-note"
              data-jr-key="correction-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Describe the correction for the filing record"
              className="mt-3 min-h-28"
            />
            {rejected && (
              <Textarea
                data-jr-key="rejected-correction-note"
                aria-label="Rejected filing correction note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Note for the rejected filing correction"
                className="mt-3 min-h-20"
              />
            )}
            <div className="mt-4 flex flex-wrap gap-3">
              <Button
                data-jr-key="correct-filing"
                variant="outline"
                disabled={!note.trim() || snapshot.matches('correcting')}
                onClick={() => actions.correct(filingId ?? '', note.trim())}
              >
                Submit correction
              </Button>
              {rejected && (
                <Button
                  data-jr-key="correct-rejected-filing"
                  disabled={!note.trim() || snapshot.matches('correctingRejected')}
                  onClick={() => actions.correctRejected(filingId ?? '', note.trim())}
                >
                  Correct rejected filing
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </FilingShell>
  );
}
